import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
  isSameDay,
} from "date-fns";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CancelIcon from "@mui/icons-material/Cancel";
import PatientService from "../../services/PatientService";
import { useTranslation } from "react-i18next";

interface MeasurementCalendarDialogProps {
  patientId: string;
  organization_id: string;
  token: string;
  open: boolean;
  onClose: () => void;
  conditionType?: string;
  engagementHistory?: any[];
}

export const MeasurementCalendarDialog: React.FC<MeasurementCalendarDialogProps> = ({
  patientId,
  organization_id,
  token,
  open,
  onClose,
  conditionType,
  engagementHistory = [],
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const getMeasurementTypeDisplayName = (measurementType: string): string => {
    const displayNameMap: { [key: string]: string } = {
      oxygen_saturation: t("SpO2"),
      perfusion_index: t("PI"),
      fev1: t("FEV1"),
      pef: t("PEF"),
      heart_rate: t("HR"),
      systolic: `${t("Systolic")} BP`,
      diastolic: `${t("Diastolic")} BP`,
      mean_arterial_pressure: t("MAP"),
      SpO2: t("SpO2"),
      PeakFlow: t("PeakFlow"),
      BloodPressure: t("BloodPressure"),
      HeartRate: t("HeartRate"),
    };

    return displayNameMap[measurementType] || t(measurementType);
  };

  // Cache object to store measurement data by month key ("yyyy-MM").
  const cacheRef = useRef<{ [monthKey: string]: { [day: string]: string[] } }>({});
  // Map day ("yyyy-MM-dd") => array of measurement detail strings.
  const [measurementsByDay, setMeasurementsByDay] = useState<{ [day: string]: string[] }>({});
  const [loading, setLoading] = useState<boolean>(false);
  // State for the currently displayed month.
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fetch measurements when the dialog opens or when the selected month changes.
  useEffect(() => {
    if (open) {
      fetchMeasurements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate]);

  const fetchMeasurements = async () => {
    setLoading(true);
    const monthKey = format(selectedDate, "yyyy-MM");
    // Check cache first.
    if (cacheRef.current[monthKey]) {
      setMeasurementsByDay(cacheRef.current[monthKey]);
      setLoading(false);
      return;
    }
    try {
      // Use the selected month for the request.
      const startDate = format(startOfMonth(selectedDate), "yyyy-MM-dd");
      const endDate = format(endOfMonth(selectedDate), "yyyy-MM-dd");
      const response = await PatientService.getMeasurementHistory(
        token,
        patientId,
        organization_id,
        startDate,
        endDate
      );
      // Build mapping of day => measurement details.
      const measurementsMap: { [day: string]: string[] } = {};
      if (response.history) {
        Object.keys(response.history).forEach((measurementType) => {
          response.history[measurementType].forEach(([timestamp, value]: [string, number]) => {
            const day = timestamp.slice(0, 10); // "yyyy-MM-dd"
            if (!measurementsMap[day]) {
              measurementsMap[day] = [];
            }
            measurementsMap[day].push(
              `${getMeasurementTypeDisplayName(measurementType)}: ${value}`,
            );
          });
        });
      }
      // Cache the result.
      cacheRef.current[monthKey] = measurementsMap;
      setMeasurementsByDay(measurementsMap);
    } catch (error) {
      console.error("Error fetching measurement history for calendar:", error);
    }
    setLoading(false);
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(selectedDate, 1);
    const now = new Date();
    // If nextMonth is after the current month, do not update.
    if (
      nextMonth.getFullYear() > now.getFullYear() ||
      (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() > now.getMonth())
    ) {
      return;
    }
    setSelectedDate(nextMonth);
  };

  // Navigator handlers
  const handlePrevMonth = () => {
    setSelectedDate((prev) => addMonths(prev, -1));
  };
  // Add keyboard navigation (left/right arrows) when dialog is open.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        handlePrevMonth();
      } else if (event.key === "ArrowRight") {
        handleNextMonth();
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate]);

  // Build calendar grid for the selected month.
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const getEngagementEntry = (dayStr: string) => {
    return engagementHistory.find((historyEntry) => {
      const date = typeof historyEntry === "string" ? historyEntry : historyEntry?.date;
      return date?.slice(0, 10) === dayStr;
    });
  };

  const getEngagementIcon = (dayStr: string) => {
    const entry = getEngagementEntry(dayStr);
    const status = (
      typeof entry === "string" ? entry : entry?.engagement ?? entry?.status
    )?.toUpperCase();

    switch (status) {
      case "OK":
      case "GREEN":
        return <CheckCircleIcon sx={{ color: "green", fontSize: 16 }} />;
      case "NOT OK":
      case "PARTIAL":
      case "YELLOW":
        return <ErrorIcon sx={{ color: "#edb400", fontSize: 16 }} />;
      case "SKIPPED":
      case "RED":
        return <CancelIcon sx={{ color: "#d32f2f", fontSize: 16 }} />;
      case "NO PLAN":
      case "NA":
      default:
        return (
          <RadioButtonUncheckedIcon sx={{ color: "gray", fontSize: 16 }} />
        );
    }
  };

  const rows: JSX.Element[] = [];
  let day = calendarStart;
  while (day <= calendarEnd) {
    const cells: JSX.Element[] = [];
    for (let i = 0; i < 7; i++) {
      const dayStr = format(day, "yyyy-MM-dd");
      const hasMeasurement = measurementsByDay.hasOwnProperty(dayStr);
      const hasEngagement = Boolean(getEngagementEntry(dayStr));
      const hasActivity = hasMeasurement || hasEngagement;
      // Check if this day is today.
      const isTodayCell = isSameDay(day, new Date());
      const cellContent = (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Box
            sx={{
              fontSize: "0.8rem",
              color: isSameMonth(day, monthStart)
                ? theme.palette.text.primary
                : "gray",
              alignSelf: "flex-start",
              pl: 0.5,
            }}
          >
            {format(day, "d")}
          </Box>
          {hasActivity && <Box sx={{ mt: 0.5 }}>{getEngagementIcon(dayStr)}</Box>}
        </Box>
      );

      cells.push(
        <TableCell
          key={dayStr}
          align="center"
          sx={{
            border: "1px solid #ccc",
            padding: 1,
            height: "50px",
            backgroundColor: isTodayCell ? theme.palette.action.selected : undefined,
          }}
        >
          {hasMeasurement ? (
            <Tooltip
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    fontSize: "0.8rem",
                  },
                },
              }}
              title={
                <Box>
                  {measurementsByDay[dayStr].map((detail, idx) => (
                    <Typography key={idx} variant="body2">
                      {detail}
                    </Typography>
                  ))}
                </Box>
              }
            >
              {cellContent}
            </Tooltip>
          ) : (
            cellContent
          )}
        </TableCell>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <TableRow key={day.toISOString()} sx={{ height: "50px" }}>
        {cells}
      </TableRow>
    );
  }

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Determine whether the next arrow should be disabled (if the selected month is current).
  const today = new Date();
  const disableNext =
    selectedDate.getFullYear() === today.getFullYear() &&
    selectedDate.getMonth() === today.getMonth();

  // Calculate the total number of days with measurements in the selected month.
  const daysCount = Object.keys(measurementsByDay).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          mt: 12,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {/* Left: Title */}
          <Typography variant="h6" sx={{ textAlign: "left" }}>
            Activity - {patientId}
          </Typography>
          {/* Right: Month Navigator */}
          <Box display="flex" alignItems="center">
            <IconButton onClick={handlePrevMonth}>
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            {/* Fixed-width container for date display */}
            <Box sx={{ width: "220px", textAlign: "center" }}>
              <Typography variant="h6" sx={{ mx: 1 }}>
                {format(selectedDate, "MMMM yyyy")}
              </Typography>
            </Box>
            <IconButton onClick={handleNextMonth} disabled={disableNext}>
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={150}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {weekDays.map((dayName) => (
                    <TableCell
                      key={dayName}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        border: "1px solid #ccc",
                        padding: 1,
                        height: "50px",
                      }}
                    >
                      {dayName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{rows}</TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          {daysCount > 0 ? (
            <Typography variant="body2" color="primary">
              {daysCount} day{daysCount > 1 ? "s" : ""} with measurements
            </Typography>
          ) : (
            <Typography variant="body2" color="error">
              No measurements recorded for this month.
            </Typography>
          )}
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeasurementCalendarDialog;
